import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useAuth } from "@h/useCtx";

interface AccountCtx {
  /** Owner whose CRM data is in scope. Always the signed-in user since the
   *  team-seat feature was removed (2026-09-14) — kept as the single place the
   *  CRM reads owner scope from, so callers don't each reach for auth. */
  activeOwnerId: string;
  /** False would hide CRM write affordances; no read-only seats exist anymore. */
  canEdit: boolean;
}

const Ctx = createContext<AccountCtx | null>(null);

export function AccountProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const self = user?.id ?? "";
  const value = useMemo(
    () => ({ activeOwnerId: self, canEdit: true }),
    [self],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useActiveAccount(): AccountCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useActiveAccount outside AccountProvider");
  return v;
}

// Non-throwing read for components reused on public surfaces — TourCard renders
// both in the dashboard and on the landing portfolio/feed, outside the provider.
// Returns null there instead of crashing.
// eslint-disable-next-line react-refresh/only-export-components
export function useActiveAccountOptional(): AccountCtx | null {
  return useContext(Ctx);
}

